import React, { useState, useEffect } from 'react';
import { fetchAllSessions } from '../../services/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import './SessionList.css';

const SessionList = ({ filters, selectedSession, onSessionSelect }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const session = await fetchAuthSession();
        setIsAuthenticated(!!session.tokens);
      } catch (err) {
        console.warn('Not authenticated:', err);
        setIsAuthenticated(false);
      }
    };
    
    checkAuth();
  }, []);

  // Load sessions data
  useEffect(() => {
    const loadSessions = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Only fetch if authenticated or in development mode
        if (isAuthenticated || process.env.NODE_ENV === 'development') {
          const data = await fetchAllSessions();
          setSessions(data);
        } else {
          setError('Please sign in to view your sessions');
        }
      } catch (err) {
        console.error('Error loading sessions:', err);
        setError('Failed to load sessions. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadSessions();
  }, [isAuthenticated]);

  // Apply filters to sessions
  const filteredSessions = sessions.filter(session => {
    // Date range filter
    if (filters.dateRange.start && new Date(session.date) < new Date(filters.dateRange.start)) {
      return false;
    }
    if (filters.dateRange.end && new Date(session.date) > new Date(filters.dateRange.end)) {
      return false;
    }
    
    // Location filter
    if (filters.locations.length > 0 && !filters.locations.includes(session.location)) {
      return false;
    }
    
    // Launch monitor filter
    if (filters.launchMonitors.length > 0 && !filters.launchMonitors.includes(session.launchMonitor)) {
      return false;
    }
    
    return true;
  });

  if (loading) {
    return <div className="session-list-loading">Loading sessions...</div>;
  }

  if (error) {
    return <div className="session-list-error">{error}</div>;
  }

  if (filteredSessions.length === 0) {
    return (
      <div className="session-list-empty">
        <p>No sessions found. {sessions.length > 0 ? 'Try adjusting your filters.' : 'Upload some data to get started!'}</p>
      </div>
    );
  }

  return (
    <div className="session-list">
      <div className="session-list-header">
        <div className="session-date">Date</div>
        <div className="session-location">Location</div>
        <div className="session-monitor">Launch Monitor</div>
        <div className="session-shots">Shots</div>
        <div className="session-avg-distance">Avg. Distance</div>
      </div>
      
      {filteredSessions.map(session => (
        <div 
          key={session.id}
          className={`session-item ${selectedSession === session.id ? 'selected' : ''}`}
          onClick={() => onSessionSelect(session.id)}
        >
          <div className="session-date">{new Date(session.date).toLocaleDateString()}</div>
          <div className="session-location">{session.location}</div>
          <div className="session-monitor">{session.launchMonitor}</div>
          <div className="session-shots">{session.shots}</div>
          <div className="session-avg-distance">{session.averageDistance} yds</div>
        </div>
      ))}
    </div>
  );
};

export default SessionList;
